// =============================================================================
// IssueCreditNotePage — issue a new credit note (owner-only)
// =============================================================================
// Steps in one page:
//   1. Pick the customer (B2B customers shown first; individual fallback)
//   2. Optionally tie it to an existing order/invoice for that customer
//   3. Pick a reason
//   4. Add lines (free-text or pulled from the linked order's lines)
//   5. Review running total → Issue
//
// URL params let other pages deep-link:
//   /credits/new?orderId=<id>  → pre-fills customer + invoice + line suggestions

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { IconButton } from '../../../components/IconButton';
import { Input } from '../../../components/Input';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { PageScaffold } from '../../../components/PageScaffold';
import { cn } from '../../../lib/cn';

type Reason =
  | 'RETURN'
  | 'PRICE_CORRECTION'
  | 'DISCOUNT_GRANTED'
  | 'CANCELLATION'
  | 'WRITE_OFF'
  | 'GOODWILL'
  | 'OTHER';

const REASONS: { key: Reason; label: string; help: string }[] = [
  { key: 'RETURN', label: 'Goods returned', help: 'Customer brought items back.' },
  {
    key: 'PRICE_CORRECTION',
    label: 'Price correction',
    help: 'Overcharged on the original invoice.',
  },
  {
    key: 'DISCOUNT_GRANTED',
    label: 'Late discount',
    help: 'Settlement / loyalty discount given after the sale.',
  },
  {
    key: 'CANCELLATION',
    label: 'Invoice cancellation',
    help: 'Order cancelled after invoice was issued.',
  },
  {
    key: 'WRITE_OFF',
    label: 'Bad-debt write-off',
    help: "Customer can't / won't pay — write it off.",
  },
  { key: 'GOODWILL', label: 'Goodwill gesture', help: 'Complaint resolution / good-faith credit.' },
  { key: 'OTHER', label: 'Other', help: 'Note the actual reason in the notes field.' },
];

interface CustomerRow {
  id: string;
  customerCode: string;
  name: string;
  companyName: string | null;
  type: 'individual' | 'company';
  phone: string;
  vatExcluded: boolean;
  vatNumber: string | null;
}

interface ShopRow {
  id: string;
  code: string;
  name: string;
}

interface OrderRow {
  id: string;
  number: string;
  total: string;
  balanceDue: string;
  vatExempt: boolean;
  status: string;
  shop: { id: string; code: string };
  invoice: { id: string; number: string } | null;
  lines: Array<{
    id: string;
    qty: number;
    unitPrice: string;
    discount: string;
    vatRate: string;
    lineTotal: string;
    product: { id: string; sku: string; name: string };
  }>;
}

interface LineDraft {
  productId?: string;
  description: string;
  qty: number;
  unitPrice: number;
  discount: number;
  vatRate: number;
}

export function IssueCreditNotePage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderIdParam = params.get('orderId') ?? '';
  const customerIdParam = params.get('customerId') ?? '';

  // ── State ────────────────────────────────────────────────────────────────
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerIdParam);
  const [selectedOrderId, setSelectedOrderId] = useState(orderIdParam);
  const [shopId, setShopId] = useState('');
  const [reason, setReason] = useState<Reason>('RETURN');
  const [vatExempt, setVatExempt] = useState(false);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([
    { description: '', qty: 1, unitPrice: 0, discount: 0, vatRate: 0.15 },
  ]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const customers = useQuery({
    queryKey: ['customers-list-for-cn'],
    queryFn: () => apiGet<CustomerRow[]>('/customers'),
  });
  const shops = useQuery({
    queryKey: ['shops-list-for-cn'],
    queryFn: () => apiGet<ShopRow[]>('/shops'),
  });
  // When a customer is selected, fetch their orders so we can link an invoice.
  const customerOrders = useQuery({
    queryKey: ['customer-orders-for-cn', selectedCustomerId],
    queryFn: () => apiGet<OrderRow[]>('/orders', { customerId: selectedCustomerId }),
    enabled: !!selectedCustomerId,
  });
  // Deep-link: if we have an orderId, fetch it directly to seed everything.
  const seedOrder = useQuery({
    queryKey: ['order-for-cn', orderIdParam],
    queryFn: () =>
      apiGet<OrderRow & { customerId: string; shopId: string; customer: CustomerRow }>(
        `/orders/${orderIdParam}`,
      ),
    enabled: !!orderIdParam,
  });

  // Apply the seed order when it loads — fill customer, shop, lines.
  useEffect(() => {
    if (!seedOrder.data) return;
    const o = seedOrder.data;
    setSelectedCustomerId(o.customerId);
    setSelectedOrderId(o.id);
    setShopId(o.shopId);
    setVatExempt(o.vatExempt);
    setLines(
      o.lines.map((l) => ({
        productId: l.product.id,
        description: `${l.product.name} (return)`,
        qty: l.qty,
        unitPrice: Number(l.unitPrice),
        discount: Number(l.discount),
        vatRate: o.vatExempt ? 0 : Number(l.vatRate),
      })),
    );
  }, [seedOrder.data]);

  // Default shop when not deep-linked: pick the first shop.
  useEffect(() => {
    if (!shopId && shops.data && shops.data.length > 0) {
      setShopId(shops.data[0]!.id);
    }
  }, [shops.data, shopId]);

  // ── Customer filter (B2B first, then individuals; search applies to both) ─
  const filteredCustomers = useMemo(() => {
    const rows = customers.data ?? [];
    const q = customerSearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter((c) => {
          const haystack =
            `${c.name} ${c.companyName ?? ''} ${c.phone} ${c.customerCode}`.toLowerCase();
          return haystack.includes(q);
        })
      : rows;
    // B2B first, then individuals
    return [...filtered].sort((a, b) => {
      if (a.type === 'company' && b.type !== 'company') return -1;
      if (b.type === 'company' && a.type !== 'company') return 1;
      return (a.companyName ?? a.name).localeCompare(b.companyName ?? b.name);
    });
  }, [customers.data, customerSearch]);

  const selectedCustomer = useMemo(
    () =>
      (customers.data ?? []).find((c) => c.id === selectedCustomerId) ??
      seedOrder.data?.customer ??
      null,
    [customers.data, selectedCustomerId, seedOrder.data],
  );

  // When customer changes (non-deep-link), set vatExempt from their flag.
  useEffect(() => {
    if (selectedCustomer && !orderIdParam) {
      setVatExempt(selectedCustomer.vatExcluded);
    }
  }, [selectedCustomer, orderIdParam]);

  // ── Totals (live) ────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let subtotal = 0,
      vatTotal = 0;
    for (const l of lines) {
      const net = l.unitPrice * l.qty - l.discount;
      subtotal += net;
      vatTotal += vatExempt ? 0 : net * l.vatRate;
    }
    return { subtotal, vatTotal, total: subtotal + vatTotal };
  }, [lines, vatExempt]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const issue = useMutation({
    mutationFn: () =>
      apiPost<{ id: string; number: string }>('/credit-notes', {
        shopId,
        customerId: selectedCustomerId,
        invoiceId: selectedOrderId
          ? ((customerOrders.data ?? []).find((o) => o.id === selectedOrderId)?.invoice?.id ??
            seedOrder.data?.invoice?.id)
          : undefined,
        reason,
        vatExempt,
        notes: notes.trim() || undefined,
        lines: lines
          .filter((l) => l.description.trim() && l.unitPrice > 0 && l.qty > 0)
          .map((l) => ({
            productId: l.productId,
            description: l.description,
            qty: l.qty,
            unitPrice: l.unitPrice,
            discount: l.discount,
            vatRate: l.vatRate,
          })),
      }),
    onSuccess: (cn) => navigate(`/credits/${cn.id}`),
    onError: (e) => setSubmitError(getApiError(e).message),
  });

  const canSubmit =
    !!selectedCustomerId &&
    !!shopId &&
    lines.some((l) => l.description.trim() && l.unitPrice > 0 && l.qty > 0) &&
    totals.total > 0 &&
    !issue.isPending;

  // ── Render ───────────────────────────────────────────────────────────────
  const FOOTER_ACTIONS = (
    <Button
      disabled={!canSubmit}
      onClick={() => {
        setSubmitError(null);
        issue.mutate();
      }}
    >
      {issue.isPending ? 'Issuing…' : 'Issue credit note'}
    </Button>
  );

  return (
    <PageScaffold
      title="Issue credit note"
      subtitle={
        <>
          Reduces a customer's AR. Posts a balanced journal entry the moment you click{' '}
          <em>Issue</em>.<span className="ml-1 text-accent font-medium">Owner-only.</span>
        </>
      }
      back={{ to: '/credits', label: 'Credit notes' }}
      width="form-wide"
      footer={FOOTER_ACTIONS}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Customer */}
          <Card title="1 · Customer">
            {selectedCustomer ? (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-ink font-medium">
                        {selectedCustomer.type === 'company'
                          ? (selectedCustomer.companyName ?? selectedCustomer.name)
                          : selectedCustomer.name}
                      </span>
                      {selectedCustomer.type === 'company' && (
                        <StatusPill tone="accent">B2B</StatusPill>
                      )}
                      {selectedCustomer.vatExcluded && (
                        <StatusPill tone="info">VAT-exempt</StatusPill>
                      )}
                    </div>
                    <div className="text-xs text-ink-3 mt-1">
                      {selectedCustomer.customerCode} · {selectedCustomer.phone}
                      {selectedCustomer.vatNumber && ` · VAT ${selectedCustomer.vatNumber}`}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCustomerId('');
                      setSelectedOrderId('');
                    }}
                  >
                    Change
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Input
                  label="Search customer"
                  placeholder="Name, company, phone, customer code…"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  hint="B2B customers (companies) appear first — they're the typical credit-note recipient."
                />
                <div className="max-h-72 overflow-y-auto border border-rule rounded">
                  {filteredCustomers.length === 0 ? (
                    <div className="text-center text-xs text-ink-3 py-6">No customers match.</div>
                  ) : (
                    <ul className="divide-y divide-rule-2">
                      {filteredCustomers.slice(0, 40).map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() => setSelectedCustomerId(c.id)}
                            className="w-full text-left px-4 py-2 hover:bg-paper-2 flex items-center justify-between gap-3"
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-ink">
                                  {c.type === 'company' ? (c.companyName ?? c.name) : c.name}
                                </span>
                                {c.type === 'company' && (
                                  <StatusPill tone="accent" className="text-[10px]">
                                    B2B
                                  </StatusPill>
                                )}
                                {c.vatExcluded && (
                                  <StatusPill tone="info" className="text-[10px]">
                                    VAT-exempt
                                  </StatusPill>
                                )}
                              </div>
                              {c.type === 'company' && c.name !== (c.companyName ?? c.name) && (
                                <div className="text-[10px] text-ink-3">Attn: {c.name}</div>
                              )}
                            </div>
                            <div className="text-xs text-ink-3 kc-mono">{c.customerCode}</div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* Step 2: Linked order (optional) */}
          {selectedCustomerId && (
            <Card
              title="2 · Original invoice (optional)"
              subtitle="Tying the CN to an invoice keeps the audit trail tight. Skip for goodwill / general credits."
            >
              {customerOrders.isLoading ? (
                <div className="text-xs text-ink-3">Loading customer's orders…</div>
              ) : (customerOrders.data ?? []).length === 0 ? (
                <div className="text-xs text-ink-3">No invoices on file for this customer.</div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setSelectedOrderId('')}
                      className={cn(
                        'px-2 py-1 rounded border',
                        !selectedOrderId
                          ? 'bg-accent text-white border-accent'
                          : 'border-rule text-ink-3 hover:border-accent/40',
                      )}
                    >
                      None (standalone credit)
                    </button>
                  </div>
                  <div className="max-h-56 overflow-y-auto border border-rule rounded divide-y divide-rule-2">
                    {(customerOrders.data ?? [])
                      .filter((o) => o.invoice)
                      .map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => setSelectedOrderId(o.id)}
                          className={cn(
                            'w-full text-left px-4 py-2 flex items-center justify-between gap-3',
                            selectedOrderId === o.id ? 'bg-accent-soft' : 'hover:bg-paper-2',
                          )}
                        >
                          <div>
                            <div className="text-sm text-ink kc-mono">{o.invoice!.number}</div>
                            <div className="text-[10px] text-ink-3 mt-0.5">
                              {o.status} · shop {o.shop.code} · balance{' '}
                              <Money value={o.balanceDue} /> of <Money value={o.total} />
                            </div>
                          </div>
                          <Money value={o.total} className="text-ink" />
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Step 3: Reason */}
          {selectedCustomerId && (
            <Card title="3 · Reason">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {REASONS.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setReason(r.key)}
                    className={cn(
                      'text-left px-3 py-2 rounded border text-sm transition',
                      reason === r.key
                        ? 'border-accent bg-accent-soft text-ink'
                        : 'border-rule text-ink-2 hover:border-accent/40',
                    )}
                  >
                    <div className="font-medium">{r.label}</div>
                    <div className="text-[10px] text-ink-3 mt-0.5">{r.help}</div>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* Step 4: Lines */}
          {selectedCustomerId && (
            <Card
              title="4 · Lines"
              subtitle={
                vatExempt
                  ? 'VAT is excluded for this customer.'
                  : 'VAT is reversed at the same rate as the original sale.'
              }
              bodyClassName="p-0"
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                    <th className="px-4 py-2 font-medium">Description</th>
                    <th className="px-3 py-2 font-medium text-right w-16">Qty</th>
                    <th className="px-3 py-2 font-medium text-right w-32">Unit</th>
                    <th className="px-3 py-2 font-medium text-right w-24">Discount</th>
                    <th className="px-3 py-2 font-medium text-right w-24">VAT %</th>
                    <th className="px-4 py-2 font-medium text-right w-32">Line total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const net = l.unitPrice * l.qty - l.discount;
                    return (
                      <tr key={idx} className="border-b border-rule-2">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={l.description}
                            onChange={(e) =>
                              setLines((ls) =>
                                ls.map((x, i) =>
                                  i === idx ? { ...x, description: e.target.value } : x,
                                ),
                              )
                            }
                            placeholder="e.g. 1× Sealy King mattress (faulty edge)"
                            className="kc-input text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={l.qty}
                            onChange={(e) =>
                              setLines((ls) =>
                                ls.map((x, i) =>
                                  i === idx
                                    ? { ...x, qty: Math.max(1, Number(e.target.value) || 1) }
                                    : x,
                                ),
                              )
                            }
                            className="kc-input text-sm text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={l.unitPrice}
                            onChange={(e) =>
                              setLines((ls) =>
                                ls.map((x, i) =>
                                  i === idx
                                    ? { ...x, unitPrice: Math.max(0, Number(e.target.value) || 0) }
                                    : x,
                                ),
                              )
                            }
                            className="kc-input text-sm text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={l.discount}
                            onChange={(e) =>
                              setLines((ls) =>
                                ls.map((x, i) =>
                                  i === idx
                                    ? { ...x, discount: Math.max(0, Number(e.target.value) || 0) }
                                    : x,
                                ),
                              )
                            }
                            className="kc-input text-sm text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          {vatExempt ? (
                            <div className="text-right text-xs text-ink-3">—</div>
                          ) : (
                            <select
                              value={String(l.vatRate)}
                              onChange={(e) =>
                                setLines((ls) =>
                                  ls.map((x, i) =>
                                    i === idx ? { ...x, vatRate: Number(e.target.value) } : x,
                                  ),
                                )
                              }
                              className="kc-input text-sm text-right"
                            >
                              <option value="0.15">15%</option>
                              <option value="0">0%</option>
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Money value={net} muted={net <= 0} />
                        </td>
                        <td className="px-2 py-2 text-right">
                          {lines.length > 1 && (
                            <IconButton
                              aria-label="Remove line"
                              variant="danger"
                              size="sm"
                              onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                            >
                              ×
                            </IconButton>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-4 py-3 border-t border-rule flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setLines((ls) => [
                      ...ls,
                      { description: '', qty: 1, unitPrice: 0, discount: 0, vatRate: 0.15 },
                    ])
                  }
                >
                  + Add line
                </Button>
                <label className="text-xs text-ink-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={vatExempt}
                    onChange={(e) => setVatExempt(e.target.checked)}
                  />
                  Treat as VAT-exempt
                </label>
              </div>
            </Card>
          )}

          {/* Notes */}
          {selectedCustomerId && (
            <Card title="Internal notes (optional)">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Will appear on the CN PDF + customer statement. Stay neutral / factual."
                className="kc-input min-h-[80px] resize-y w-full text-sm"
              />
            </Card>
          )}
        </div>

        {/* Right: totals + issue */}
        <div className="space-y-4">
          <Card
            title="Credit total"
            subtitle={
              vatExempt ? 'No VAT (customer exempt)' : 'VAT-3 register will reflect the reversal'
            }
          >
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-ink-3">Subtotal</span>
                <Money value={totals.subtotal} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-ink-3">VAT</span>
                {vatExempt ? (
                  <span className="text-ink-3">—</span>
                ) : (
                  <Money value={totals.vatTotal} />
                )}
              </div>
              <div className="border-t border-rule pt-2 mt-2 flex items-baseline justify-between">
                <span className="text-ink font-medium">Total credit</span>
                <span className="text-xl kc-serif text-accent">
                  <Money value={totals.total} />
                </span>
              </div>
            </div>
            {submitError && <div className="text-xs text-negative mt-3">{submitError}</div>}
            <p className="text-[10px] text-ink-3 mt-2">
              Posts immediately. A reversing JE can void it later if a mistake is found.
            </p>
          </Card>

          {shops.data && shops.data.length > 1 && (
            <Card title="Issued at">
              <select
                value={shopId}
                onChange={(e) => setShopId(e.target.value)}
                className="kc-input text-sm w-full"
              >
                {shops.data.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} — {s.name}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-ink-3 mt-1">
                CN number prefix uses this shop's code (e.g. CN-
                {shops.data.find((s) => s.id === shopId)?.code ?? 'XX'}-).
              </p>
            </Card>
          )}
        </div>
      </div>
    </PageScaffold>
  );
}
